import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Drawer,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { TuneOutlined, SearchOffOutlined } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { loadListings } from "../api/publicApi";
import ListingCard from "../components/ListingCard";
import FilterPanel, { EMPTY_FILTERS, applyFilters } from "../components/FilterPanel";
import { tokens, radius } from "../../theme/theme";
import { plural } from "../utils/format";

const PAGE_SIZE = 12;

function filtersFromParams(params) {
  const next = Object.assign({}, EMPTY_FILTERS);
  ["q", "rooms", "region", "heating", "priceFrom", "priceTo", "sort"].forEach((key) => {
    const value = params.get(key);
    if (value) next[key] = value;
  });
  next.furniture = params.get("furniture") === "1";
  next.technology = params.get("technology") === "1";
  return next;
}

function paramsFromFilters(filters) {
  const params = {};
  ["q", "rooms", "region", "heating", "priceFrom", "priceTo"].forEach((key) => {
    if (filters[key]) params[key] = filters[key];
  });
  if (filters.furniture) params.furniture = "1";
  if (filters.technology) params.technology = "1";
  if (filters.sort && filters.sort !== "new") params.sort = filters.sort;
  return params;
}

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

  useEffect(() => {
    let alive = true;
    loadListings()
      .then((result) => {
        if (alive) setItems(result);
      })
      .catch((err) => {
        console.error(err);
        if (alive) setError(err);
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleFiltersChange = useCallback(
    (next) => {
      setVisible(PAGE_SIZE);
      setSearchParams(paramsFromFilters(next), { replace: true });
    },
    [setSearchParams]
  );

  const districts = useMemo(() => {
    const seen = {};
    (items || []).forEach((item) => {
      if (item.region) seen[item.region] = true;
    });
    return Object.keys(seen).sort(function (a, b) {
      return a.localeCompare(b, "uk");
    });
  }, [items]);

  const results = useMemo(() => applyFilters(items || [], filters), [items, filters]);
  const shown = results.slice(0, visible);

  const panel = (
    <FilterPanel
      filters={filters}
      onChange={handleFiltersChange}
      districts={districts}
      resultCount={results.length}
    />
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="overline" sx={{ color: tokens.blue600 }}>
          Каталог
        </Typography>
        <Typography variant="h1" sx={{ fontSize: { xs: "2rem", md: "2.6rem" } }}>
          Квартири в оренду у Львові
        </Typography>
        {items !== null && (
          <Typography variant="body1" color="text.secondary">
            {results.length}{" "}
            {plural(results.length, "варіант", "варіанти", "варіантів")} за
            вашими критеріями
          </Typography>
        )}
      </Stack>

      <Button
        variant="outlined"
        startIcon={<TuneOutlined />}
        onClick={() => setDrawerOpen(true)}
        sx={{ display: { xs: "inline-flex", md: "none" }, mb: 2 }}
      >
        Фільтри
      </Button>

      <Grid container spacing={3}>
        <Grid item md={3} sx={{ display: { xs: "none", md: "block" } }}>
          {panel}
        </Grid>

        <Grid item xs={12} md={9}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: radius.md, mb: 2 }}>
              Не вдалося завантажити базу квартир. Оновіть сторінку або спробуйте
              трохи пізніше.
            </Alert>
          )}

          <Grid container spacing={2.5}>
            {items === null &&
              !error &&
              [0, 1, 2, 3, 4, 5].map((n) => (
                <Grid item xs={12} sm={6} lg={4} key={n}>
                  <Skeleton variant="rounded" height={330} sx={{ borderRadius: radius.lg }} />
                </Grid>
              ))}

            {shown.map((item) => (
              <Grid item xs={12} sm={6} lg={4} key={item.id}>
                <ListingCard item={item} />
              </Grid>
            ))}
          </Grid>

          {items !== null && results.length === 0 && !error && (
            <Stack
              alignItems="center"
              spacing={1.5}
              sx={{ py: 8, textAlign: "center", color: tokens.ink500 }}
            >
              <SearchOffOutlined sx={{ fontSize: 44, color: tokens.ink400 }} />
              <Typography variant="h5" sx={{ color: tokens.ink }}>
                За такими фільтрами нічого немає
              </Typography>
              <Typography variant="body2" sx={{ maxWidth: 420 }}>
                Спробуйте розширити діапазон ціни або зняти обмеження по району —
                база оновлюється щодня.
              </Typography>
              <Button variant="outlined" onClick={() => handleFiltersChange(EMPTY_FILTERS)}>
                Скинути фільтри
              </Button>
            </Stack>
          )}

          {visible < results.length && (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button
                size="large"
                variant="outlined"
                onClick={() => setVisible(visible + PAGE_SIZE)}
              >
                Показати ще {Math.min(PAGE_SIZE, results.length - visible)}
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>

      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            maxHeight: "88vh",
            borderTopLeftRadius: radius.xl,
            borderTopRightRadius: radius.xl,
            p: 2,
          },
        }}
      >
        {panel}
        <Button
          fullWidth
          variant="contained"
          size="large"
          sx={{ mt: 2 }}
          onClick={() => setDrawerOpen(false)}
        >
          Показати {results.length}
        </Button>
      </Drawer>
    </Container>
  );
}
